import { CloseOutlined, DeleteOutlined, EditOutlined, EyeOutlined, SaveOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import { Button } from "antd";
import { tenantLocationAtom } from "atoms/tenantLocationAtom.ts";
import { DrawerLink } from "components/UI/DrawerLink";
import { usePermission } from "hooks/usePermissions";
import { useContext } from "react";
import { useRecoilValue } from "recoil";
import { Identifiable } from "types/models";

import { TableAction } from "../../types/table";
import { EditableContext } from "../DragAndDrop/context";

interface ActionButtonsProps<T extends Identifiable> {
    actions: TableAction<T>[];
    record: T;
    handleSave: (record: T) => void;
    handleCancel: (record: T) => void;
    handleEdit: (record: T) => void;
    editing: boolean;
    defaultDelete?: (record: T) => Promise<void>;
}

export const ActionButtons = <T extends Identifiable>({
    actions,
    defaultDelete,
    editing,
    handleEdit,
    handleSave,
    handleCancel,
    record,
}: ActionButtonsProps<T>) => {
    const form = useContext(EditableContext);
    const permissionsUtils = usePermission();

    const onSave = async () => {
        try {
            const updatedRecord = (await form?.validateFields()) as object;
            handleSave({ ...record, ...updatedRecord });
        } catch (errInfo) {
            console.log("Save failed:", errInfo);
        }
    };

    const visibleActions = actions.filter((action) => (action.visible ? action.visible(record) : true));

    return (
        <div className={"flex gap-4 flex-row items-center"}>
            {visibleActions.map((action, index) => {
                if (action.permission && !permissionsUtils.hasPermission(action.permission)) {
                    return null;
                }

                return (
                    <span key={index}>
                        <ActionButton
                            action={action}
                            record={record}
                            defaultDelete={defaultDelete ?? (() => Promise.resolve())}
                            handleSave={onSave}
                            handleCancel={handleCancel}
                            handleEdit={handleEdit}
                            editing={editing}
                        />
                    </span>
                );
            })}
        </div>
    );
};

interface ActionButtonProps<T extends Identifiable> {
    action: TableAction<T>;
    record: T;
    defaultDelete: (record: T) => Promise<void>;
    handleSave: (record: T) => void;
    handleCancel: (record: T) => void;
    handleEdit: (record: T) => void;
    editing: boolean;
}

const ActionButton = <T extends Identifiable>({
    action,
    record,
    defaultDelete,
    handleSave,
    handleCancel,
    handleEdit,
    editing,
}: ActionButtonProps<T>) => {
    const tenantLocationState = useRecoilValue(tenantLocationAtom);
    let drawerTo: string | undefined;

    if (action.type === "drawer" && action.drawer) {
        drawerTo = typeof action.drawer.to === "function" ? action.drawer.to(record) : action.drawer.to;
    }

    const handleClick = () => (action.onClick ? action.onClick(record) : null);

    switch (action.type) {
        case "detail":
            return (
                <Link
                    to={`${tenantLocationState.fullWithSlug}/${record.id ? record.id : ""}${
                        action.addToLink ? `/${action.addToLink}` : ""
                    }`}
                >
                    {renderLabelOrIcon(action, record, <EyeOutlined />)}
                </Link>
            );
        case "drawer":
            return (
                action.drawer && (
                    <DrawerLink
                        to={drawerTo}
                        action="show"
                        drawerId={action.drawer.drawerId}
                        disabled={action.drawer.disabled}
                        search={action?.drawer?.search?.(record)}
                    >
                        {renderLabelOrIcon(action, record, action.icon)}
                    </DrawerLink>
                )
            );
        case "edit":
            return editing ? (
                <div className={"flex gap-2 items-center p-2 bg-gray-200 rounded-md"}>
                    <Button className={"bg-transparent p-0 border-none"} onClick={() => handleSave(record)}>
                        <SaveOutlined />
                    </Button>
                    <Button className={"bg-transparent p-0 border-none"} onClick={() => handleCancel(record)}>
                        <CloseOutlined />
                    </Button>
                </div>
            ) : (
                <Button className={"bg-transparent p-0 border-none"} onClick={() => handleEdit(record)}>
                    {renderLabelOrIcon(action, record, <EditOutlined />)}
                </Button>
            );
        case "delete":
            return (
                <Button
                    className={"bg-transparent p-0 border-none"}
                    onClick={() => (action.onClick ? action.onClick(record) : defaultDelete(record))}
                >
                    {renderLabelOrIcon(action, record, <DeleteOutlined />)}
                </Button>
            );
        case "customButton":
            return (
                <Button onClick={handleClick} className={"bg-transparent border-none p-0"}>
                    {renderLabelOrIcon(action, record, action.icon)}
                </Button>
            );
        case "custom":
            return <>{renderLabelOrIcon(action, record, action.icon)}</>;
        default:
            return null;
    }
};

const renderLabelOrIcon = <T extends Identifiable>(
    action: TableAction<T>,
    record: T,
    defaultIcon: React.ReactNode
): React.ReactNode => {
    if (typeof action.label === "function") {
        return action.label(record);
    }
    return action.label || defaultIcon;
};
